import type { ServerMessage, TreasureKind } from "@xiuxian/game-protocol";
import { formatScoreValue, formatTreasureName } from "./game-formatters.js";
import {
  type Locale,
  type TranslationKey,
  type TranslationParams,
  translate,
} from "./index.js";

export type RuntimeStatusKey =
  | "default"
  | "entryReady"
  | "bootstrapFailed"
  | "restartPrompt"
  | "restartFailed"
  | "connecting"
  | "websocketOpen"
  | "transportError"
  | "disconnectedRetry"
  | "manualDisconnect"
  | "reconnecting"
  | "protocolParseFailed"
  | "joinedRoom"
  | "syncedTick"
  | "deadRestart"
  | "spectatingKiller"
  | "eventReceived"
  | "serverErrorReturned";

export type RuntimeLogKey =
  | "started"
  | "connect"
  | "helloSent"
  | "transportError"
  | "close"
  | "respawnRequested"
  | "nonTextIgnored"
  | "protocolError"
  | "handshake"
  | "reconnectIn"
  | "serverError"
  | "serviceWorkerFailed"
  | "pwaStorageUnavailable"
  | "playerKeyStorageUnavailable"
  | "identityStorageUnavailable"
  | "audioStorageUnavailable"
  | "leaderboardStorageUnavailable"
  | "nameConfirmed"
  | "restartConfirmed"
  | "startupFailed"
  | "restartFailed";

export type DebugToggleKey =
  | "formationLayers"
  | "background"
  | "stones"
  | "minimap"
  | "playerSprites";

const STATUS_KEYS: Record<RuntimeStatusKey, TranslationKey> = {
  default: "runtime.status.default",
  entryReady: "runtime.status.entryReady",
  bootstrapFailed: "runtime.status.bootstrapFailed",
  restartPrompt: "runtime.status.restartPrompt",
  restartFailed: "runtime.status.restartFailed",
  connecting: "runtime.status.connecting",
  websocketOpen: "runtime.status.websocketOpen",
  transportError: "runtime.status.transportError",
  disconnectedRetry: "runtime.status.disconnectedRetry",
  manualDisconnect: "runtime.status.manualDisconnect",
  reconnecting: "runtime.status.reconnecting",
  protocolParseFailed: "runtime.status.protocolParseFailed",
  joinedRoom: "runtime.status.joinedRoom",
  syncedTick: "runtime.status.syncedTick",
  deadRestart: "runtime.status.deadRestart",
  spectatingKiller: "runtime.status.spectatingKiller",
  eventReceived: "runtime.status.eventReceived",
  serverErrorReturned: "runtime.status.serverErrorReturned",
};

const LOG_KEYS: Record<RuntimeLogKey, TranslationKey> = {
  started: "runtime.log.started",
  connect: "runtime.log.connect",
  helloSent: "runtime.log.helloSent",
  transportError: "runtime.log.transportError",
  close: "runtime.log.close",
  respawnRequested: "runtime.log.respawnRequested",
  nonTextIgnored: "runtime.log.nonTextIgnored",
  protocolError: "runtime.log.protocolError",
  handshake: "runtime.log.handshake",
  reconnectIn: "runtime.log.reconnectIn",
  serverError: "runtime.log.serverError",
  serviceWorkerFailed: "runtime.log.serviceWorkerFailed",
  pwaStorageUnavailable: "runtime.log.pwaStorageUnavailable",
  playerKeyStorageUnavailable: "runtime.log.playerKeyStorageUnavailable",
  identityStorageUnavailable: "runtime.log.identityStorageUnavailable",
  audioStorageUnavailable: "runtime.log.audioStorageUnavailable",
  leaderboardStorageUnavailable: "runtime.log.leaderboardStorageUnavailable",
  nameConfirmed: "runtime.log.nameConfirmed",
  restartConfirmed: "runtime.log.restartConfirmed",
  startupFailed: "runtime.log.startupFailed",
  restartFailed: "runtime.log.restartFailed",
};

const TREASURE_KINDS = new Set<TreasureKind>([
  "qiankun_ruler",
  "hunyuan_fan",
  "spirit_gourd",
  "thunder_sword_box",
  "myriad_sword_token",
  "wind_thunder_boots",
  "black_tortoise_armor",
  "ascension_pearl",
]);

export function formatRuntimeStatus(
  locale: Locale,
  key: RuntimeStatusKey,
  params: TranslationParams = {},
): string {
  return translate(locale, STATUS_KEYS[key], params);
}

export function formatRuntimeError(
  locale: Locale,
  key: "transport" | "nonTextMessage",
): string {
  return translate(locale, `runtime.error.${key}` as TranslationKey);
}

export function formatRuntimeLog(
  locale: Locale,
  key: RuntimeLogKey,
  params: TranslationParams = {},
): string {
  return translate(locale, LOG_KEYS[key], params);
}

export function formatFallbackCultivatorName(locale: Locale): string {
  return translate(locale, "runtime.fallbackCultivator");
}

export function formatServerEventLog(
  locale: Locale,
  message: Extract<ServerMessage, { type: "server_event" }>,
): string {
  const payload = message.payload;
  switch (message.event) {
    case "attack_started":
      return translate(locale, "runtime.event.attackStarted", {
        attackerId: displayPayloadId(payload.attackerId),
        targetId: displayPayloadId(payload.targetId),
      });
    case "attack_hit":
      return translate(locale, "runtime.event.attackHit", {
        attackerId: displayPayloadId(payload.attackerId),
        targetId: displayPayloadId(payload.targetId),
      });
    case "attack_missed":
      return translate(locale, "runtime.event.attackMissed", {
        attackerId: displayPayloadId(payload.attackerId),
        targetId: displayPayloadId(payload.targetId),
      });
    case "pickup":
      return translate(locale, "runtime.event.pickup", {
        playerId: displayPayloadId(payload.playerId),
        value: displayPayloadNumber(payload.value),
      });
    case "death": {
      const killer = payload.killerId
        ? translate(locale, "runtime.event.deathKiller", {
            killerId: displayPayloadId(payload.killerId),
          })
        : "";
      const dropped =
        typeof payload.droppedSpiritStoneCount === "number"
          ? translate(locale, "runtime.event.deathDropped", {
              count: payload.droppedSpiritStoneCount,
            })
          : "";
      return translate(locale, "runtime.event.death", {
        playerId: displayPayloadId(payload.playerId),
        killer,
        dropped,
      });
    }
    case "drop":
      return translate(locale, "runtime.event.drop", {
        playerId: displayPayloadId(payload.playerId),
        count: displayPayloadNumber(payload.droppedSpiritStoneCount),
      });
    case "respawn":
      return translate(locale, "runtime.event.respawn", {
        playerId: displayPayloadId(payload.playerId),
      });
    case "ascension":
      return translate(locale, "runtime.event.ascension", {
        playerId: displayPayloadId(payload.playerId),
        spiritPower: displayPayloadNumber(payload.spiritPower),
      });
    case "ascension_started":
      return translate(locale, "runtime.event.ascensionStarted", {
        name: displayPayloadText(payload.name),
        scoreGain: displayPayloadNumber(payload.scoreGain),
      });
    case "treasure_spawned":
      return translate(locale, "runtime.event.treasureSpawned", {
        kind: displayTreasureKind(locale, payload.kind),
      });
    case "treasure_picked":
      return translate(locale, "runtime.event.treasurePicked", {
        kind: displayTreasureKind(locale, payload.kind),
      });
    case "treasure_guard":
      return translate(locale, "runtime.event.treasureGuard", {
        playerId: displayPayloadId(payload.playerId),
        remainingCharges: displayPayloadNumber(payload.remainingCharges),
      });
    case "world_reborn":
      return translate(locale, "runtime.event.worldReborn", {
        ascenderName: displayPayloadText(payload.ascenderName),
      });
    default:
      return translate(locale, "runtime.event.serverEvent", {
        event: message.event,
      });
  }
}

export function formatRoomEventLog(
  locale: Locale,
  message: Extract<ServerMessage, { type: "server_room_event" }>,
): string {
  const payload = message.payload;
  switch (message.event) {
    case "player_joined":
      return translate(locale, "runtime.roomEvent.playerJoined", {
        name: displayPayloadText(payload.name),
      });
    case "player_left":
      return translate(locale, "runtime.roomEvent.playerLeft", {
        name: displayPayloadText(payload.name),
      });
    case "owner_transferred":
      return translate(locale, "runtime.roomEvent.ownerTransferred", {
        ownerName: displayPayloadText(payload.ownerName),
      });
    case "room_started":
      return translate(locale, "runtime.roomEvent.roomStarted");
    case "room_closed":
      return translate(locale, "runtime.roomEvent.roomClosed");
    case "match_ended":
      return translate(locale, "runtime.roomEvent.matchEnded", {
        winnerName: displayPayloadText(payload.winnerName),
      });
    case "player_kicked":
      return translate(locale, "runtime.roomEvent.playerKicked");
    case "invite_reset":
      return translate(locale, "runtime.roomEvent.inviteReset");
    case "rematch":
      return translate(locale, "runtime.roomEvent.rematch");
  }
}

export function formatAscensionAnnouncement(
  locale: Locale,
  options: { name: string; totalScore: number | null },
): string {
  if (options.totalScore === null) {
    return translate(locale, "runtime.ascension.announcementNoScore", {
      name: options.name,
    });
  }
  return translate(locale, "runtime.ascension.announcementWithScore", {
    name: options.name,
    totalScore: formatScoreValue(locale, options.totalScore),
  });
}

export function formatWorldRebornAnnouncement(
  locale: Locale,
  ascenderName: string,
): string {
  return translate(locale, "runtime.ascension.worldRebornAnnouncement", {
    name: ascenderName,
  });
}

export function formatTreasureSpawnTitle(locale: Locale): string {
  return translate(locale, "runtime.treasureNotice.spawnTitle");
}

export function formatTreasurePickupTitle(locale: Locale): string {
  return translate(locale, "runtime.treasureNotice.pickupTitle");
}

export function formatTreasureGuardTitle(locale: Locale): string {
  return translate(locale, "runtime.treasureNotice.guardTitle");
}

export function formatTreasureGuardBody(
  locale: Locale,
  remainingCharges: number,
  isSelf: boolean,
): string {
  return translate(
    locale,
    isSelf
      ? "runtime.treasureNotice.guardSelfBody"
      : "runtime.treasureNotice.guardOtherBody",
    { remainingCharges },
  );
}

export function formatAscensionNotice(
  locale: Locale,
  name: string,
): {
  title: string;
  body: string;
} {
  return {
    title: translate(locale, "runtime.treasureNotice.ascensionTitle"),
    body: translate(locale, "runtime.treasureNotice.ascensionBody", { name }),
  };
}

export function formatTreasureNoticeIconText(locale: Locale): string {
  return translate(locale, "runtime.treasureNotice.iconText");
}

export function formatAscensionOverlay(
  locale: Locale,
  options: {
    ascenderName: string;
    scoreGain: number;
    totalScore: number;
    isSelf: boolean;
  },
): { title: string; subtitle: string; seal: string } {
  return {
    title: options.isSelf
      ? translate(locale, "runtime.ascension.selfTitle")
      : translate(locale, "runtime.ascension.otherTitle", {
          name: options.ascenderName,
        }),
    subtitle: options.isSelf
      ? translate(locale, "runtime.ascension.selfSubtitle", {
          scoreGain: formatScoreValue(locale, options.scoreGain),
          totalScore: formatScoreValue(locale, options.totalScore),
        })
      : translate(locale, "runtime.ascension.otherSubtitle"),
    seal: translate(locale, "runtime.ascension.seal"),
  };
}

export function formatAscensionCountdownUnit(locale: Locale): string {
  return translate(locale, "runtime.ascension.countdownUnit");
}

export function formatWorldRebornOverlay(
  locale: Locale,
  name: string,
): {
  seal: string;
  title: string;
  subtitle: string;
} {
  return {
    seal: translate(locale, "runtime.ascension.rebornSeal"),
    title: translate(locale, "runtime.ascension.rebornTitle"),
    subtitle: translate(locale, "runtime.ascension.rebornSubtitle", { name }),
  };
}

export function formatControlHint(
  locale: Locale,
  device: "mobile" | "desktop",
): string {
  return translate(locale, `runtime.hint.${device}` as TranslationKey);
}

export function formatDebugToggleLog(
  locale: Locale,
  key: DebugToggleKey,
  value: string | number | boolean,
): string {
  return translate(locale, `runtime.debug.${key}` as TranslationKey, {
    count: value,
    mode: formatDebugMode(locale, value),
  });
}

function formatDebugMode(
  locale: Locale,
  value: string | number | boolean,
): string {
  if (value === "simplified" || value === true) {
    return translate(locale, "runtime.debug.simplified");
  }
  if (value === "full" || value === false) {
    return translate(locale, "runtime.debug.full");
  }
  if (value === "shown") {
    return translate(locale, "runtime.debug.shown");
  }
  if (value === "hidden") {
    return translate(locale, "runtime.debug.hidden");
  }
  return String(value);
}

function displayTreasureKind(locale: Locale, value: unknown): string {
  return typeof value === "string" && isTreasureKind(value)
    ? formatTreasureName(locale, value)
    : displayPayloadText(value);
}

function isTreasureKind(value: string): value is TreasureKind {
  return TREASURE_KINDS.has(value as TreasureKind);
}

function displayPayloadId(value: unknown): string {
  return typeof value === "string" && value.length > 0
    ? value.slice(0, 8)
    : "-";
}

function displayPayloadNumber(value: unknown): string {
  return typeof value === "number" ? Math.floor(value).toString() : "-";
}

function displayPayloadText(value: unknown): string {
  return typeof value === "string" && value.length > 0 ? value : "-";
}
